import React from "react";
import { Badge, CircularProgress, Grid, IconButton, Stack, Toolbar, Typography, Zoom } from "@mui/material";
import { CloseRounded, MenuRounded, Minimize, Notifications, OpenInFullRounded } from "@mui/icons-material";
import { useAiChat } from "../../provider/AiChatProvider";
import { CollapsableWindowProps } from "../CollapsableWindow/CollapsableWindow";
import aiChatToolbarStyles from "./aiChatToolbarStyles.ts";
import { useCase } from "../../provider/CaseProvider.tsx";
import useAiChatReasoningStatus from "../../hooks/useAiChatReasoningStatus.ts";

type AiChatToolbarProps = {
  windowState: CollapsableWindowProps["windowState"];
  setWindowState: React.Dispatch<React.SetStateAction<CollapsableWindowProps["windowState"]>>;
};

export default function AiChatToolbar({ windowState, setWindowState }: AiChatToolbarProps) {
  const { setHistoryOpen, activeAgent, resetAiChat } = useAiChat();
  const { product } = useCase();
  const { indicatorType, setIndicatorType } = useAiChatReasoningStatus();

  if (!product) {
    return null;
  }

  return (
    <Toolbar
      sx={{
        background: (theme) =>
          `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.accent.main} 100%)`,
      }}
    >
      <Grid container sx={{ width: "100%" }}>
        <Grid sx={{ flexGrow: 1 }}>
          <Stack direction={"row"} spacing={2} sx={{ alignItems: "center" }}>
            <IconButton
              onClick={() => {
                setWindowState("maximized");
                setHistoryOpen((prevState) => !prevState);
              }}
            >
              <MenuRounded sx={{ color: "primary.contrastText" }} />
            </IconButton>
            <Typography sx={{ color: "primary.contrastText" }}>
              <strong>{activeAgent}</strong>
            </Typography>
            {windowState === "minimized" && (
              <>
                {indicatorType === "loading" && (
                  <CircularProgress size={"1rem"} sx={{ color: "primary.contrastText" }} />
                )}
                <Zoom in={indicatorType === "alert"} timeout={{ enter: 500, exit: 0 }} unmountOnExit>
                  <Badge variant={"dot"} color={"error"}>
                    <Notifications sx={aiChatToolbarStyles.bell} />
                  </Badge>
                </Zoom>
              </>
            )}
          </Stack>
        </Grid>
        <Grid>
          {windowState === "maximized" && (
            <IconButton onClick={() => setWindowState("minimized")}>
              <Minimize sx={{ color: "primary.contrastText" }} />
            </IconButton>
          )}
          {windowState === "minimized" && (
            <IconButton
              onClick={() => {
                setIndicatorType(null);
                setWindowState("maximized");
              }}
            >
              <OpenInFullRounded sx={{ color: "primary.contrastText" }} />
            </IconButton>
          )}
        </Grid>
        <Grid>
          <IconButton onClick={() => resetAiChat()}>
            <CloseRounded sx={{ color: "primary.contrastText" }} />
          </IconButton>
        </Grid>
      </Grid>
    </Toolbar>
  );
}
